import { useRouter } from "next/router";
import { forwardRef, useMemo } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { section as sectionData } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { PageType } from "shared/lib/page-type";

import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { DefaultHeaderSwapThreshold } from "scmp-app/components/header/consts";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import { useSwapOnScrollEffect } from "scmp-app/components/header/hooks";
import {
  getArticleLayout,
  getDefaultLayout,
  getHomeMiniLayout,
  getPostMagazineArticleLayout,
  getPostMagazineLayout,
  getStyleArticleLayout,
  getStyleLayout,
  getVideoLayout,
} from "scmp-app/components/header/layouts";
import {
  Container,
  HeaderContainer,
  LeftFeatureContainer,
  RightFeatureContainer,
  StyleHeaderLogoContainer,
} from "scmp-app/components/header/styles";
import type { HeaderConfiguration } from "scmp-app/components/header/types";
import { HeaderLogo } from "scmp-app/components/section/section-style/styles";

import { getSectionLayout } from "./layouts/section";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
  isHomeMiniHeader?: boolean;
};

type HeaderState =
  | "article"
  | "default"
  | "homeMiniHeader"
  | "postMagazine"
  | "postMagazineArticle"
  | "section"
  | "style"
  | "styleArticle"
  | "subSection"
  | "video";

// Helper Hook to keep main component clean
const useHeaderState = (
  headerConfiguration: HeaderConfiguration | undefined,
  isHomeMiniHeader: boolean | undefined,
) => {
  const { query } = useRouter();
  const currentPageType = useCurrentPageType();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isArticlePage = currentPageType === PageType.Article;
  const isSectionStyleMainPage =
    currentPageType === PageType.Section && query.entityId === sectionData.style.entityId;
  const isMagazineStyle = headerConfiguration?.appBarVariant === "scmp/magazines-style";
  const isPostMagazine = headerConfiguration?.appBarVariant === "scmp/postmag";
  const isSection = currentPageType === PageType.Section;
  const isSubSection = currentPageType === PageType.SubSection;
  const isVideo =
    currentPageType === PageType.VideoHomepage || currentPageType === PageType.VideoSection;

  const { shouldSwap } = useSwapOnScrollEffect(
    (headerConfiguration?.appBarStickyScrollHeight ?? 0) || DefaultHeaderSwapThreshold,
  );

  const headerState: HeaderState = useMemo(() => {
    if (isMagazineStyle) {
      return isArticlePage ? "styleArticle" : "style";
    }
    if (isArticlePage && isPostMagazine) return "postMagazineArticle";
    if (isArticlePage) return "article";
    if (isHomeMiniHeader) return "homeMiniHeader";
    if (isPostMagazine) return "postMagazine";
    if (isSection) return "section";
    if (isSubSection) return "subSection";
    if (isVideo) return "video";
    return "default";
  }, [
    isArticlePage,
    isHomeMiniHeader,
    isMagazineStyle,
    isPostMagazine,
    isSection,
    isSubSection,
    isVideo,
  ]);

  return {
    headerState,
    isArticlePage,
    isMagazineStyle,
    isPostMagazine,
    isSectionStyleMainPage,
    reference,
    sectionId: isSection || isSubSection ? query.entityId : undefined,
    shouldSwap,
  };
};

export const Header = forwardRef<HTMLDivElement, Props>(function Header(
  { className, headerConfiguration, initEdition, isHomeMiniHeader },
  forwardedReference,
) {
  const { headerState, isMagazineStyle, isSectionStyleMainPage, reference, sectionId, shouldSwap } =
    useHeaderState(headerConfiguration, isHomeMiniHeader);

  const features = useHeaderFeaturesProvider();

  const layout = useMemo(() => {
    const props = {
      config: headerConfiguration,
      features,
      initEdition,
      shouldSwap,
    };

    switch (headerState) {
      case "article":
        return getArticleLayout(props);
      case "homeMiniHeader":
        return getHomeMiniLayout(props);
      case "postMagazine":
        return getPostMagazineLayout(props);
      case "postMagazineArticle":
        return getPostMagazineArticleLayout(props);
      case "section":
      case "subSection":
        return getSectionLayout({
          ...props,
          isSubSection: headerState === "subSection",
          sectionId,
        });
      case "style":
        return getStyleLayout(props);
      case "styleArticle":
        return getStyleArticleLayout(props);
      case "video":
        return getVideoLayout(props);
      default:
        return getDefaultLayout(props);
    }
  }, [headerConfiguration, features, initEdition, shouldSwap, headerState, sectionId]);

  return (
    <Container $isMagazineStyle={isMagazineStyle} className={className} ref={forwardedReference}>
      {isSectionStyleMainPage && (
        <StyleHeaderLogoContainer ref={reference}>
          <HeaderLogo />
        </StyleHeaderLogoContainer>
      )}
      <HeaderContainer>
        <LeftFeatureContainer
          $isPostMagazine={headerState === "postMagazine"}
          $isPostMagazineArticle={headerState === "postMagazineArticle"}
          $shouldSwap={shouldSwap}
        >
          <ClientSideSuspense>{layout.left}</ClientSideSuspense>
        </LeftFeatureContainer>

        {/* Note: If Center contains dynamic/user data, wrap it in ClientSideSuspense too */}
        {layout.center}

        <RightFeatureContainer $shouldSwap={headerState === "article" && shouldSwap}>
          <ClientSideSuspense>{layout.right}</ClientSideSuspense>
        </RightFeatureContainer>
      </HeaderContainer>
    </Container>
  );
});

Header.displayName = "Header";
